import {
  FC,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import { Form, message } from 'antd';

import { HeaderTabCivil } from '../../components/TabsPropertyDataCivil/components';
import {
  LOADING_SAVED_QUOTATIONS_STATES,
  PLAN_TYPE,
  QuotationStep,
} from '../../../../../shared';
import { TabsPropertyDataCivil } from '../../components';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import {
  GenericDataFormContext,
  QuotationInfoContext,
} from '../../../../../shared/contexts';
import { StepTwoFormContext } from './StepTwoFormContext';

import {
  Products,
  SelectOption,
} from '../../../../../../../../../shared/interfaces';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';

const planTypeEquivalent: { A: boolean; C: boolean } = {
  A: true,
  C: false,
};

export type StepTwoFormFields = {
  [key: `${'plan-tab' | 'province-tab' | 'location-tab'}-${number}`]: string;
};

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    currentStep,
    loadSaveQuotationState,
    quotationInfo,
    savedQuotation,
    setLoadSaveQuotationState,
    renewalQuotationState,
    setRenewalQuotationState,
    quotationToRenewal
  } = useContext(QuotationInfoContext);
  const {
    canShowSpinner,
    getLocalitiesPerProvince,
    setCanShowSpinner,
    setPlansSelected,
    updateFormsAfterRemoveTabs,
    removedTabIndexes, 
    setRemovedTabIndexes,
    setUserCanDeleteTabs
  } = useContext(GenericDataFormContext);

  const INITIAL_KEY_TAB = 1;
  // Cobertura de bien único: el backend del PDF solo toma getBien()
  const MAX_TABS = 1;

  const [areTabActionsAllowed, setAreTabActionsAllowed] =
    useState<boolean>(true);
  const [isPlanTypeADisabled, setIsPlanTypeADisabled] = useState(false);
  const [isPlanTypeCDisabled, setIsPlanTypeCDisabled] = useState(false);
  const [keyTabToDelete, setKeyTabToDelete] = useState<number | null>(null);
  const [planOptionsBySelectedPlan, setPlanOptionsBySelectedPlan] = useState<
    Array<SelectOption>
  >([]);
  const [plansByActivityList, setPlansByActivityList] = useState<
    Array<Products>
  >([]);
  const [planTypeSelected, setPlanTypeSelected] = useState<PLAN_TYPE>(
    PLAN_TYPE.OPEN
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [civilFormValues, setCivilFormValues] =
    useState<StepTwoFormFields | null>(null);
  const [tabs, setTabs] = useState<Array<any>>([]);

  const [stepTwoForm] = Form.useForm<any>();

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const addNewTab = useCallback(() => {
    if (tabs.length < MAX_TABS) {
      const newActiveKey = ++newTabIndex.current;
      const isTabActionEnabled = planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];

      if(removedTabIndexes.includes(newActiveKey)) {
        setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
      };

      setTabs([
        ...tabs,
        {
          label: (
            <HeaderTabCivil
              id={newActiveKey}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataCivil id={newActiveKey} />,
          key: newActiveKey,
          closable: false,
          forceRender: true,
        },
      ]);
      setActiveKey(newActiveKey);

      return;
    }

    message.error('Solo puede agregar 1 Responsabilidad Civil (cobertura de bien único).');
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey, 
    planTypeSelected,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);

  const duplicateTab = useCallback(
    (id: any) => {
      if (tabs.length < MAX_TABS) {
        const tabSelected = tabs.find((tab: any) => tab.key === id);
        const newActiveKey = ++newTabIndex.current;
        const isTabActionEnabled =
          planTypeEquivalent[planTypeSelected ?? PLAN_TYPE.CLOSED];

        if (tabSelected) {
          const newTab = {
            ...tabSelected,
            key: newActiveKey,
            label: (
              <HeaderTabCivil
                id={newActiveKey}
                areTabActionsAllowed={isTabActionEnabled}
              />
            ),
            children: (
              <TabsPropertyDataCivil id={newActiveKey} duplicateId={id} />
            ),
            closable: false,
            forceRender: true,
          };
          setTabs([...tabs, newTab]);
          setActiveKey(newActiveKey);
          if(removedTabIndexes.includes(newActiveKey)) {
            setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
          };
        }

        return;
      }

      message.error('Solo puede agregar 1 Responsabilidad Civil (cobertura de bien único).');
    },
    [
      tabs, 
      newTabIndex, 
      setTabs, 
      setActiveKey, 
      planTypeSelected,
      removedTabIndexes,
      setRemovedTabIndexes,
    ]
  );

  const removeTab = useCallback(
    (targetKey: number) => {
      setCanShowSpinner(true);

      setTimeout(() => {
        updateFormsAfterRemoveTabs([targetKey], [stepTwoForm]);

        setRemovedTabIndexes((prevValues) => [...prevValues, targetKey]);

        const targetIndex = tabs.findIndex(
          (pane: any) => pane.key === targetKey
        );
        const newPanes = tabs
          .filter((pane: any) => pane.key !== targetKey)
          .map((tab, index) => ({
            ...tab,
            key: index + 1,
            label: (
              <HeaderTabCivil
                id={index + 1}
                areTabActionsAllowed={areTabActionsAllowed}
              />
            ),
            children: <TabsPropertyDataCivil id={index + 1} />,
          }));

        if (newPanes.length && targetKey === activeKey) {
          const { key } =
            newPanes[
              targetIndex === newPanes.length ? targetIndex - 1 : targetIndex
            ];
          setActiveKey(key);
        }

        newTabIndex.current = tabs.length - 1;
        setTabs(newPanes);
        setCanShowSpinner(false);
      }, 500);
    },
    [tabs, setTabs, setActiveKey, activeKey]
  );

  const resetTabs = useCallback(
    (unconfirmedPlanType: PLAN_TYPE) => {
      const isTabActionEnabled = planTypeEquivalent[unconfirmedPlanType];
      setTabs([
        {
          label: (
            <HeaderTabCivil
              id={INITIAL_KEY_TAB}
              areTabActionsAllowed={isTabActionEnabled}
            />
          ),
          children: <TabsPropertyDataCivil id={INITIAL_KEY_TAB} />,
          key: INITIAL_KEY_TAB,
          closable: false,
          forceRender: true,
        },
      ]);

      stepTwoForm.resetFields();
      newTabIndex.current = INITIAL_KEY_TAB;
      setActiveKey(INITIAL_KEY_TAB);
    },
    [tabs, setTabs, setActiveKey]
  );

  const getListPlansActivity = async (article: string, branch: string) => {
    if (plansByActivityList.length) return;
    setCanShowSpinner(true);

    try {
      const response = await QuotationService.getProductsListPerProducer(
        article,
        branch
      );

      if (response?.data?.productosPorProductor?.length) {
        const productosPorProductor = response?.data?.productosPorProductor;

        setPlansByActivityList(productosPorProductor);

        const hasPlanTypeA = productosPorProductor.some(
          (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
        );

        setPlanTypeSelected(hasPlanTypeA ? PLAN_TYPE.OPEN : PLAN_TYPE.CLOSED);
      }
    } catch {
    } finally {
      setCanShowSpinner(false);
    }
  };

  const filterPlansByPlanTypeSelected = useCallback(() => {
    if (planTypeSelected) {
      const selectedPlanTypeValue: PLAN_TYPE = planTypeSelected;

      if (!civilFormValues && !canShowSpinner) {
        setTabs([
          {
            label: (
              <HeaderTabCivil
                id={INITIAL_KEY_TAB}
                areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]}
              />
            ),
            children: <TabsPropertyDataCivil id={INITIAL_KEY_TAB} />,
            key: INITIAL_KEY_TAB,
            closable: false,
            forceRender: true,
          },
        ]);
      }

      setAreTabActionsAllowed(planTypeEquivalent[selectedPlanTypeValue]);

      const filteredPlans = plansByActivityList.filter(
        (plan) => plan.tipoDePlan === selectedPlanTypeValue
      );

      const plansOptions = filteredPlans.map((el) => ({
        label: `${el.codigo} - ${el.descripcionLarga}`,
        value: el.codigo,
      }));

      setPlanOptionsBySelectedPlan(plansOptions);
    }
  }, [plansByActivityList, planTypeSelected]);

  const fillStepTwoServices = useCallback(async (isRenewal: boolean) => {
    try {
      let quotationToProccess;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }

      const promises: Array<Promise<void>> = [
        getListPlansActivity(quotationInfo!.article, quotationInfo!.branch),
      ];
      const provincesSelected: Array<string> = [];
      const plansSelected: Array<string> = [];

      quotationToProccess.bienes.forEach((bien: any) => {
        if (!provincesSelected.includes(bien.provincia)) {
          provincesSelected.push(bien.provincia);
        }

        if (!plansSelected.includes(String(bien.planSeleccionado))) {
          plansSelected.push(String(bien.planSeleccionado));
        }
      });

      provincesSelected.forEach((province) =>
        promises.push(getLocalitiesPerProvince(province))
      );
      setPlansSelected(plansSelected);

      await Promise.all(promises).then(() => {
        if(isRenewal){
          setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL);
        }else{
          setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED)
        }
      }
      );
    } catch (error) {}
  }, [
    quotationInfo,
    savedQuotation,
    quotationToRenewal,
    getListPlansActivity,
    getLocalitiesPerProvince,
    setLoadSaveQuotationState,
    setPlansSelected,
  ]);

  const fillStepTwoData = useCallback(async (isRenewal: boolean) => {
    const _tabs: any = [];
    const _inputs: any = [];
    const selectedPlanTypeValue: PLAN_TYPE = planTypeSelected;
    setUserCanDeleteTabs(false);
    let quotationToProccess;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
      quotationToProccess.bienes.forEach((bien: any, index: number) => {
      const _activeKey = index + 1;
      newTabIndex.current = _activeKey;

      _tabs.push({
        label: (
          <HeaderTabCivil
            id={_activeKey}
            areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]}
          />
        ),
        children: <TabsPropertyDataCivil id={_activeKey} />,
        key: _activeKey,
        closable: false,
        forceRender: true,
      });

      _inputs.push({
        name: `plan-tab-${_activeKey}`,
        value: `${bien.planSeleccionado}`,
      });
      _inputs.push({
        name: `province-tab-${_activeKey}`,
        value: `${bien.provincia}`,
      });
      _inputs.push({
        name: `location-tab-${_activeKey}`,
        value: `${bien.sufijoPostal}${bien.codigoPostal}`,
      });
      _inputs.push({
        name: `propertyDataAddress${_activeKey}`,
        value: `${bien.domicilio}`,
      });
    });

    setTabs([..._tabs]);
    setActiveKey(INITIAL_KEY_TAB);
    stepTwoForm.setFields(_inputs);
    setCivilFormValues(_inputs);
  }, [
    savedQuotation,
    quotationToRenewal,
    setActiveKey,
    setTabs,
    stepTwoForm,
    setCivilFormValues,
    newTabIndex,
    setUserCanDeleteTabs,
  ]);

  useEffect(() => {
    if (
      !civilFormValues &&
      quotationInfo &&
      currentStep === QuotationStep.TWO
    ) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [quotationInfo, currentStep, civilFormValues, getListPlansActivity]);

  useEffect(() => {
    if (plansByActivityList.length) {
      const hasPlanTypeA = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.OPEN
      );
      const hasPlanTypeC = plansByActivityList.some(
        (plan) => plan.tipoDePlan === PLAN_TYPE.CLOSED
      );

      setIsPlanTypeADisabled(!hasPlanTypeA);
      setIsPlanTypeCDisabled(!hasPlanTypeC);
    }
  }, [plansByActivityList]);

  useEffect(() => {
    if (!civilFormValues && plansByActivityList.length && planTypeSelected) {
      filterPlansByPlanTypeSelected();
    }
  }, [
    filterPlansByPlanTypeSelected,
    planTypeSelected,
    plansByActivityList.length,
  ]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING
      );
      fillStepTwoServices(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING
      );
      fillStepTwoData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      tabs.length === savedQuotation.bienes.length
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
      );
    }
  }, [loadSaveQuotationState, savedQuotation, tabs]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL
      );
      fillStepTwoServices(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL
      );
      fillStepTwoData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {    
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL &&
      tabs.length === quotationToRenewal.bienes.length
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
      );
    }
  }, [renewalQuotationState, savedQuotation, tabs]);

  const values = useMemo(
    () => ({
      activeKey,
      addNewTab,
      areTabActionsAllowed,
      civilFormValues,
      duplicateTab,
      filterPlansByPlanTypeSelected,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
      keyTabToDelete,
      planOptionsBySelectedPlan,
      planTypeSelected,
      removeTab,
      resetTabs,
      setActiveKey,
      setCivilFormValues,
      setKeyTabToDelete,
      setPlanTypeSelected,
      setShowModal,
      setTabs,
      showModal,
      stepTwoForm,
      tabs,
    }),
    [
      activeKey,
      addNewTab,
      areTabActionsAllowed,
      civilFormValues,
      duplicateTab,
      filterPlansByPlanTypeSelected,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
      keyTabToDelete,
      planOptionsBySelectedPlan,
      planTypeSelected,
      removeTab,
      resetTabs,
      setActiveKey,
      setCivilFormValues,
      setKeyTabToDelete,
      setPlanTypeSelected,
      setShowModal,
      setTabs,
      showModal,
      stepTwoForm,
      tabs,
    ]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};
